import { useEffect, useMemo, useRef, useState } from "react";
import { Search, LocateFixed, ChevronRight, ChevronDown, Plus, TriangleAlert, Check } from "lucide-react";
import { TopBar, DestinationRow, CoachRibbon, ActionButton, SecondaryAction } from "./kit";
import MapFullScreen from "./kit/MapFullScreen";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import LogMatchAddGroundPanel from "./LogMatchAddGroundPanel";
import { ARCHIVED_LABEL, isArchived } from "../lib/archived";
import { track } from "../lib/usageTracker";
import { tapHalfLead } from "./kit/tapTarget";
import { useAuth } from "../context/AuthContext";
import { usePageWidth } from "../context/PageWidthContext";
import useLaptop, { useTablet, usePhone } from "../lib/useLaptop";
import GroundsSheet, { SheetFloat } from "./GroundsSheet";
import { homeLabelForRole, homePathForRole } from "../lib/homePath";
import { NEAR_ME_BUSY_LABEL, NEAR_ME_LABEL, NEAR_ME_RADIUS_KM } from "../lib/nearMe";
import {
  COACH_ADDED_EMPTY,
  COACH_FIND_TITLE,
  COACH_NO_MATCH_HINT,
  DIRECTORY_FAILED_BODY,
  DIRECTORY_FAILED_TITLE,
  GROUNDS_PLAYED_EMPTY,
  GROUNDS_PLAYED_HELPER,
  NOTHING_NEAR_ACTION,
  NOTHING_NEAR_TITLE,
  coachAddedHeader,
  coachFactsLine,
  coachNoMatchLine,
  groundFactsLine,
  groundSavedLine,
  groupPlayedByLetter,
  moreFooter,
  nearbyDistanceLabel,
  nearbyFactChips,
  nextPresentLetter,
  playedCountLabel,
  railNeeded,
} from "../lib/groundsNearYou";

/**
 * #1394 (D221 G4, D354): "Clear it" sits inside a sentence, as a button. A button cannot be a
 * split inline box, so it is an inline-block whose padding makes the 44px hit area and whose
 * equal negative margin gives the padding back to the line: the sentence keeps its height and
 * the word stays where it was drawn. The sideways padding is also given back, so no word moves.
 */
const CLEAR_IT_STYLE = {
  display: "inline-block",
  paddingBlock: `calc(-1 * ${tapHalfLead()})`,
  marginBlock: `calc${tapHalfLead()}`,
  paddingInline: "calc(var(--nw-size-tap) / 4)",
  marginInline: "calc(var(--nw-size-tap) / -4)",
  background: "none",
  border: "none",
  cursor: "pointer",
  font: "inherit",
  color: "var(--nw-navy)",
  fontWeight: 600,
  textDecoration: "underline",
};

// #1220 — the two choices GroundsAddGround.dc.html draws inside "Add a ground".
const ADD_MENU_ITEMS = [
  { key: "here", mode: "location", label: "Where I'm standing", hint: "Uses your phone's location for the pin" },
  { key: "elsewhere", mode: "name", label: "Somewhere else", hint: "Name it and drop the pin on the map" },
];

// One definition of the bar's end controls, shared by the list and the
// add-a-ground step (#1125): two copies is how the step ended up without them.
// #887 review round (B5, merged from #765/#858): this bar joins the same
// anatomy every other navy TopBar draws: `compact` (18px glyph, D160's own tap
// area) and `empty` (the drawn 26px account circle), both matching
// `RecordEmptyScreen.jsx`'s own call.
const BAR_END_CONTROLS = (
  <>
    <NotificationsBell compact surface="white" />
    <AccessibilityMenu hideTextSize surface="white" />
    <AccountMenu empty surface="white" hasRibbon />
  </>
);

/**
 * Issue #765 — "grounds near you" (wave 5, slice 1). Behind
 * `newUi("grounds") && isPlayer` (PR #858 F6), replaces `/grounds-map` for
 * a signed-in PLAYER, and (#1288, `audience="coach"`) a coach. An admin never
 * reaches this component: `GroundsMap.jsx`'s own gate keeps them on the
 * shipped page unconditionally on the flag.
 *
 * PR #858 review round 1 (F3): `mapSurface`/`mapNoticeElement`/
 * `searchResultLine` are PRE-RENDERED JSX from `GroundsMap.jsx` — the
 * shipped directory map (its own fit, viewport-bound fetch, facilities
 * layer, attribution, marker taps), its own `grounds-map-notice` overlay,
 * and the shipped #617 search-result line, all reused unchanged rather
 * than rebuilt here. `mapNoticeElement` may instead be a function of the
 * full-screen map's `{ closeThen }` (PR #1273 F1), so its "Add a ground"
 * closes full screen before the add dialog opens; otherwise the dialog sat
 * under the overlay. `term`/`setTerm` is the SAME shared search box that
 * drives them (F5) — this screen's own played-list narrowing reads it too.
 *
 * Row-tap-to-detail — issue #766, D167 G3: a played/nearby row with a
 * `ground_id` opens `GroundCard.jsx` (`onOpenGround`, threaded down from
 * `GroundsMap.jsx`); a free-text played row (no `ground_id`, #765's own
 * `rowId` fallback) stays inert, same as it always has. A marker's own
 * Leaflet popup (inside `mapSurface`, self-contained) is still the whole
 * of what "keep marker taps" means for the map — G3 names that
 * deliberately unchanged.
 */
export default function GroundsNearYouScreen({
  // #1288: "coach" draws the round-12 CoachGrounds list (Grounds you added in
  // place of Grounds you've played); the player's screen is byte-identical.
  audience = "player",
  addedAll = [],
  addedNarrowed = [],
  addedTruncated = false,
  playedAll,
  playedNarrowed,
  playedRail,
  loaded = true,
  nearMe,
  useMyLocation,
  clearNearMe,
  nearbyList,
  newGroundIds,
  contentFailed,
  reload,
  term,
  // #1405 — the raw text for the field's `value`. `term` is trimmed upstream
  // (GroundsMap.jsx), so a field fed `term` swallows the trailing space the
  // moment it is typed. Falls back to `term` for callers that pass one string.
  searchText,
  setTerm,
  searchPending,
  searchResultLine,
  // #1411 (D367.19): the Played / Near me / All chips, pre-rendered by the host that owns the view.
  viewChips = null,
  mapNoticeElement,
  mapSurface,
  ensureFacilities,
  onOpenGround,
  onGroundAdded,
  onUseExistingGround,
  onAddSurfaceInstead,
  addRequest = null,
  onAddRequestClose,
  // #1430 (D386): from the laptop tier the opened ground's card sits in the detail column under the map; the
  // host passes it only then (below that the card replaces this screen, as it always has).
  detailCard = null,
}) {
  // #1220 — which way into the add panel the player took: "location" ("Where
  // I'm standing") or "name" ("Somewhere else", the empty-nearby link). The
  // host's `addRequest` ({ seed, pin }) is the other two: Search worldwide
  // and a dropped pin; the host owns it because the map and the search line
  // live in the host.
  const [addMode, setAddMode] = useState(null);
  const addingGround = addMode !== null || Boolean(addRequest);
  // #1430: from the laptop tier the list beside the map takes the wide column (the tablet keeps the one column
  // at 640); the add step is a form and keeps the narrow one.
  const laptop = useLaptop();
  usePageWidth(laptop && !addingGround ? "wide" : "narrow");
  // The page takes its wide width a render after this screen mounts, but from the laptop tier the grid is already
  // two columns, so a map mounted now would measure the narrow column and tile only that. From the laptop tier
  // the map therefore mounts one tick later, when the column is its final width; below it (and in a render with
  // no media query) it mounts at once, as it always did.
  // #1481: the tablet tier (768 to 1023) has the same trouble with one column: the band measured before the page took
  // its final width and tiled 491 of 598px. So the late mount runs from 768, not only from the laptop tier.
  const tablet = useTablet();
  const phone = usePhone();
  const [sheetFull, setSheetFull] = useState(false);
  const [mapMounted, setMapMounted] = useState(!tablet);
  useEffect(() => {
    // The add step replaces the screen and narrows the page: the map is drawn again, a tick after the page has widened.
    if (addingGround && tablet) setMapMounted(false);
    if (addingGround || mapMounted) return undefined;
    const id = setTimeout(() => setMapMounted(true), 0);
    return () => clearTimeout(id);
  }, [mapMounted, addingGround, tablet]);
  const closeAdd = () => {
    setAddMode(null);
    onAddRequestClose?.();
  };
  // Counted once per tap on any way into adding a ground; nothing typed is read.
  // Not a hook: the host passes the near-me action under this name.
  const locate = useMyLocation;
  // #1143 part 2: the name of the ground the add step just saved, said under
  // the title until the next action (a search, opening a ground, adding again).
  // The coach's screen (#1288) keeps the panel's toast and draws no line.
  const [savedName, setSavedName] = useState(null);
  const openGround = onOpenGround
    ? (g) => {
        setSavedName(null);
        onOpenGround(g);
      }
    : undefined;
  useEffect(() => {
    setSavedName(null);
  }, [term]);
  useEffect(() => {
    if (addingGround) setSavedName(null);
  }, [addingGround]);
  // Once the step has closed and the list is back, scroll to the new row. If
  // Nearby is not showing it (S1) there is no row and nothing scrolls.
  const scrollToAfterAdd = useRef(null);
  useEffect(() => {
    if (addingGround || !scrollToAfterAdd.current) return undefined;
    const id = scrollToAfterAdd.current;
    scrollToAfterAdd.current = null;
    const raf = requestAnimationFrame(() => {
      document.querySelector(`[data-testid="grounds-near-nearby-row-${id}"]`)?.scrollIntoView({ block: "center" });
    });
    return () => cancelAnimationFrame(raf);
  }, [addingGround]);
  const startAdding = (mode = "name") => {
    track("grounds.add");
    setAddMode(mode);
  };
  // The first keystroke in the search box is counted once per visit, never the text.
  const searchedRef = useRef(false);
  const countSearch = () => {
    if (searchedRef.current) return;
    searchedRef.current = true;
    track("grounds.search");
  };

  // PR #858 review round 1 (F1) — a REAL window, not a rendered-then-hidden
  // list: `groupPlayedByLetter` below runs over `visibleRows`
  // (`playedNarrowed.slice(0, revealCount)`) only, so the DOM never carries
  // more rows than are actually shown. Resets to the drawn cap whenever the
  // search term changes, so a narrower answer does not stay stuck open at
  // a previous reveal.
  const coach = audience === "coach";
  const [revealCount, setRevealCount] = useState(PLAYED_LIST_VISIBLE_CAP_LOCAL);
  useEffect(() => {
    setRevealCount(PLAYED_LIST_VISIBLE_CAP_LOCAL);
  }, [term]);

  const pendingScrollLetter = useRef(null);
  // F11 — the timer id backing `suppressScrollTrackingRef`'s own window;
  // declared beside the ref it clears so a rail jump fired again before
  // the previous one's timer expired replaces it instead of stacking.
  const jumpSuppressTimeout = useRef(null);
  useEffect(() => {
    if (!pendingScrollLetter.current) return;
    const letter = pendingScrollLetter.current;
    pendingScrollLetter.current = null;
    const raf = requestAnimationFrame(() => {
      // PR #858 review round 3 (F2) — `block: "start"`, not `"nearest"`.
      // "Nearest" only moves the viewport as far as the row's NEAREST edge
      // — for a row already fully below the fold that is its BOTTOM edge,
      // which lands the row at the bottom of the screen rather than
      // bringing it to where the rail's own jump promises: the top. That
      // held even once the reveal above had grown the DOM to include a
      // letter that was not on screen a moment ago, and while its own facts
      // line is still loading (the row exists in the DOM as soon as it is
      // revealed; the facts line is a later, async fill on top of it).
      document.querySelector(`[data-grounds-letter="${letter}"]`)?.scrollIntoView({ block: "start" });
    });
    // eslint-disable-next-line consistent-return
    return () => cancelAnimationFrame(raf);
  }, [revealCount]);

  const visibleRows = useMemo(() => playedNarrowed.slice(0, revealCount), [playedNarrowed, revealCount]);
  const { groups: visibleGroups, unlettered: visibleUnlettered } = useMemo(() => groupPlayedByLetter(visibleRows), [visibleRows]);
  const footer = useMemo(() => moreFooter(playedNarrowed, revealCount), [playedNarrowed, revealCount]);

  // D165 — "the current letter in navy": which rail letter is highlighted.
  // Review round 3, final narrow round (A3), F11: this is now the rail's
  // own EXPLICIT value, not purely a scroll-position read — set directly
  // by a tap/drag/keyboard choice (`jumpToLetter` below), and ONLY
  // passively updated by plain page scrolling (mouse wheel, touch drag on
  // the list itself, not the rail) in between explicit choices. Two bugs
  // the old, purely-scroll-derived version had:
  //   - a tap on a SHORT page (nothing to scroll to) never moved the
  //     scroll position at all, so the highlight silently stayed wherever
  //     it was — the tapped letter was never shown as current;
  //   - the scroll scan considered EVERY `data-grounds-letter` element,
  //     including an UNLETTERED row's own (a digit/symbol name's raw
  //     first character, e.g. "[") — landing there fed a non-A-Z value
  //     straight into `aria-valuetext`, which F11 explicitly forbids.
  // Starts at "A" (never `null`) so `aria-valuetext` never has to fall
  // back to placeholder text like "top of the list" (also forbidden).
  const [selectedLetter, setSelectedLetter] = useState("A");
  // Suppresses the passive scroll listener for the duration of a
  // `scrollIntoView` WE triggered (tap/drag/keyboard) — otherwise that
  // programmatic scroll's own scroll events would immediately overwrite
  // the explicit choice with whatever letter the RESOLVED (jumped-to) row
  // happens to sit at, exactly the "arrow keys must track their own
  // index, not the scroll-derived highlight" bug F11 names.
  const suppressScrollTrackingRef = useRef(false);
  useEffect(() => {
    const RAIL_TOP = 96;
    const compute = () => {
      if (suppressScrollTrackingRef.current) return;
      const rows = document.querySelectorAll("[data-grounds-letter]");
      let found = null;
      rows.forEach((el) => {
        const letter = el.getAttribute("data-grounds-letter");
        // Only a real A-Z letter counts — an unlettered row's own raw
        // first character (a digit or symbol) is never a valid rail
        // value (F11: "never '['").
        if (!/^[A-Z]$/.test(letter)) return;
        if (el.getBoundingClientRect().top <= RAIL_TOP) found = letter;
      });
      if (found) setSelectedLetter(found);
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [visibleRows, nearbyList]);

  // PR #858 review round 3 (B1) — facts lines load LAZILY, per row actually
  // on screen, rather than one fan-out over the whole played list at mount
  // (`useGroundsNearYou`'s own header has the request-count evidence). This
  // window IS "visible" for this screen's purposes: `visibleRows` is already
  // the real, capped DOM window (F1) — revealing more (a rail jump, "N
  // more") grows it, and each growth asks only for the newly-revealed ids,
  // because `ensureFacilities` itself skips anything already known or
  // already in flight.
  useEffect(() => {
    if (!ensureFacilities) return;
    const ids = (coach ? addedNarrowed : visibleRows).map((g) => g.ground_id).filter(Boolean);
    if (ids.length) ensureFacilities(ids);
  }, [visibleRows, addedNarrowed, coach, ensureFacilities]);

  /**
   * F1/F2: "scrolling or tapping reveals the rest" — a rail letter beyond
   * the current window expands the window to include it (then scrolls),
   * one already inside it just scrolls.
   *
   * D165: "a letter with no grounds is muted but still active, and
   * tapping it jumps to the next group that exists" — `nextPresentLetter`
   * resolves an empty tap/drag position to the next real target first.
   * Review round 3 (F4): the rail indexes played AND nearby, so a letter
   * that exists only in Nearby (never windowed/capped the way played rows
   * are) is found by the SAME `data-grounds-letter` query, once the
   * played-only search below comes up empty.
   *
   * Review round 3, final narrow round (A3), F11: `rawLetter` — the
   * letter a tap/drag/keyboard press actually CHOSE — is what
   * `selectedLetter` becomes, immediately, regardless of whether it has
   * anything. Only the SCROLL TARGET resolves through `nextPresentLetter`
   * ("jumps the list to the next letter that exists"); the slider's own
   * value stays on the letter that was chosen, not the one landed on.
   * `suppressScrollTrackingRef` keeps the passive scroll listener above
   * from clobbering that choice with the resolved target's own position
   * once the triggered `scrollIntoView`/reveal settles.
   */
  const jumpToLetter = (rawLetter) => {
    setSelectedLetter(rawLetter);
    suppressScrollTrackingRef.current = true;
    window.clearTimeout(jumpSuppressTimeout.current);
    jumpSuppressTimeout.current = window.setTimeout(() => {
      suppressScrollTrackingRef.current = false;
    }, 900);

    const letter = nextPresentLetter(rawLetter, playedRail);
    if (!letter) return; // nothing from here on has anything — nowhere to scroll
    const idx = playedNarrowed.findIndex((g) => (g.name || "").trim().charAt(0).toUpperCase() === letter);
    if (idx !== -1) {
      if (idx < revealCount) {
        // F2, same reason as the reveal effect above: the row's TOP goes to
        // the top of the list area, not merely into view from whichever edge
        // is closer.
        document.querySelector(`[data-grounds-letter="${letter}"]`)?.scrollIntoView({ block: "start" });
      } else {
        pendingScrollLetter.current = letter;
        setRevealCount(playedNarrowed.length);
      }
      return;
    }
    // Not in the played list at all — a nearby-only letter. Nearby rows
    // are never windowed, so a direct scroll is enough.
    document.querySelector(`[data-grounds-letter="${letter}"]`)?.scrollIntoView({ block: "start" });
  };

  const revealAll = () => setRevealCount(playedNarrowed.length);

  if (addingGround) {
    return (
      <div data-testid="grounds-near" style={{ color: "var(--nw-ink)" }}>
        <span data-testid="grounds-new-screen" className="sr-only" aria-hidden="true" />
        <ScreenBar coach={coach} />
        <LogMatchAddGroundPanel
          seed={addRequest ? addRequest.seed : term}
          startWith={addMode === "location" ? "location" : "name"}
          presetPin={addRequest?.pin ?? null}
          onUseExisting={onUseExistingGround}
          onAddSurfaceInstead={onAddSurfaceInstead}
          onCancel={closeAdd}
          pageFeedback={!coach}
          onSaved={(ground, info) => {
            // #1143: the host folds the saved row into the nearby list, the
            // search and the map before the list comes back. A ground the
            // player picked from the duplicate check is not new, so it is not
            // folded in as one.
            const created = info?.created !== false;
            if (created) onGroundAdded?.(ground);
            closeAdd();
            if (coach) return;
            setSavedName(ground.name);
            if (created && ground.ground_id) scrollToAfterAdd.current = ground.ground_id;
          }}
        />
      </div>
    );
  }

  const savedBanner = (
    <>
        {savedName && (
          <div
            role="status"
            data-testid="grounds-added-saved"
            className="flex items-center"
            style={{ gap: 10, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderLeft: "3px solid var(--nw-navy)", borderRadius: 10 }}
          >
            <Check size={16} strokeWidth={2.5} style={{ color: "var(--nw-navy)", flexShrink: 0 }} aria-hidden="true" />
            <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: "var(--nw-ink)" }}>{groundSavedLine(savedName)}</span>
          </div>
        )}

    </>
  );
  const searchBox = (
    <>
        <div
          className="flex items-center"
          style={{
            gap: 10,
            minHeight: 48,
            padding: "0 14px",
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-control)",
          }}
        >
          <Search size={18} style={{ color: "var(--nw-ink-faint)" }} aria-hidden="true" />
          <input
            data-testid="grounds-search"
            placeholder="Search a ground or a town"
            value={searchText ?? term}
            onChange={(e) => {
              countSearch();
              setTerm(e.target.value);
            }}
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
              fontSize: 15,
              color: "var(--nw-ink)",
            }}
          />
        </div>

    </>
  );
  const listBlock = (
    <>
        {contentFailed ? (
          <DirectoryFailedCard onRetry={reload} />
        ) : coach ? (
          <AddedSection
            loaded={loaded}
            added={addedAll}
            narrowed={addedNarrowed}
            truncated={addedTruncated}
            term={term}
            searching={Boolean(term) && searchPending}
            onOpenGround={openGround}
          />
        ) : (
          <PlayedSection
            playedTotal={playedAll.length}
            groups={visibleGroups}
            unlettered={visibleUnlettered}
            rail={playedRail}
            loaded={loaded}
            showRail={railNeeded(playedAll.length + nearbyList.length)}
            selectedLetter={selectedLetter}
            footer={footer}
            onRevealAll={revealAll}
            onJumpToLetter={jumpToLetter}
            searching={Boolean(term) && searchPending}
            term={term}
            playedNarrowedCount={playedNarrowed.length}
            onClearTerm={() => setTerm("")}
            onOpenGround={openGround}
          />
        )}

        <NearbySection
          nearMe={nearMe}
          nearbyList={nearbyList}
          newGroundIds={coach ? undefined : newGroundIds}
          onAddHere={() => startAdding("name")}
          onOpenGround={openGround}
        />

        <div className="flex flex-col" style={{ gap: 8 }}>
          {/* #1119 (D230.2) one Add a ground control; #1220 it opens the
              drawn menu of two (GroundsAddGround.dc.html). Radix
              DropdownMenu, modal={false}, as AccountMenu and
              UpcomingAddMenu do. */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="grounds-add-btn"
                className="flex items-center justify-center"
                style={{
                  gap: 8,
                  minHeight: 48,
                  background: "var(--nw-surface)",
                  border: "1px solid var(--nw-navy)",
                  borderRadius: "var(--nw-radius-control)",
                  fontSize: 15,
                  fontWeight: 600,
                  color: "var(--nw-navy)",
                  cursor: "pointer",
                }}
              >
                <Plus size={18} strokeWidth={2} aria-hidden="true" />
                Add a ground
                <ChevronDown size={16} strokeWidth={2.5} aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            {/* The board's menu: a bordered, rounded box of two 56px bordered
                rows, each with a trailing chevron-right. */}
            <DropdownMenuContent
              align="start"
              collisionPadding={8}
              className="p-0 shadow-none w-[var(--radix-dropdown-menu-trigger-width)]"
              style={{ border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)", background: "var(--nw-surface)", overflow: "hidden" }}
              data-testid="grounds-add-menu"
            >
              {ADD_MENU_ITEMS.map((o) => (
                <DropdownMenuItem
                  key={o.key}
                  onSelect={() => startAdding(o.mode)}
                  className="cursor-pointer flex items-center justify-between"
                  style={{
                    gap: 10,
                    minHeight: 56,
                    padding: "8px 14px",
                    background: "var(--nw-surface)",
                    border: "1px solid var(--nw-line)",
                    borderRadius: "var(--nw-radius-card)",
                  }}
                  data-testid={`grounds-add-${o.key}`}
                >
                  <span className="min-w-0 flex flex-col" style={{ gap: 2 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.25, color: "var(--nw-ink)" }}>{o.label}</span>
                    <span style={{ fontSize: 12, lineHeight: 1.25, color: "var(--nw-ink-muted)" }}>{o.hint}</span>
                  </span>
                  <ChevronRight size={18} strokeWidth={2} style={{ color: "var(--nw-navy)", flexShrink: 0 }} aria-hidden="true" />
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {coach || detailCard ? (
          // D301 Q3: the coach face carries no gold, so "Use my location" is
          // the boxed secondary action (the position is still read on a press).
          // #1430 / R-003: beside an opened ground's card the card's "Log a match here" is the one gold on the
          // screen, so the player's list takes the same boxed action while a card is open.
          <SecondaryAction
            data-testid="grounds-near-me-btn"
            onClick={() => {
              track("grounds.near_me");
              locate();
            }}
            disabled={nearMe.status === "locating"}
            style={{ gap: 8, width: "100%", color: "var(--nw-ink)" }}
          >
            <LocateFixed size={20} color="var(--nw-ink)" aria-hidden="true" />
            {nearMe.status === "locating" ? NEAR_ME_BUSY_LABEL : NEAR_ME_LABEL}
          </SecondaryAction>
        ) : (
          <ActionButton
            data-testid="grounds-near-me-btn"
            onClick={() => {
              track("grounds.near_me");
              locate();
            }}
            disabled={nearMe.status === "locating"}
            icon={<LocateFixed size={20} color="var(--nw-ink)" />}
            style={{ color: "var(--nw-ink)" }}
          >
            {nearMe.status === "locating" ? NEAR_ME_BUSY_LABEL : NEAR_ME_LABEL}
          </ActionButton>
        )}

        {nearMe.status === "error" && (
          // D55/D57 — the failure stays on the page, carries the alert
          // glyph and left rule, and fires no toast.
          <div
            role="alert"
            data-testid="grounds-near-me-message"
            className="flex items-start"
            style={{ gap: 8, borderLeft: "3px solid var(--nw-alert)", paddingLeft: 10 }}
          >
            <TriangleAlert size={14} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
            <p className="nw-help" style={{ color: "var(--nw-ink)" }}>{nearMe.message}</p>
          </div>
        )}
    </>
  );

  // #1621 (D439): below 768px the screen is a full-screen map with a draggable list sheet over it. The same search,
  // chips and list, not a second copy of them; the map is not wrapped in `MapFullScreen`, so the pill draws no full-screen button.
  if (phone) {
    const notice = typeof mapNoticeElement === "function" ? mapNoticeElement({ closeThen: (fn) => fn() }) : mapNoticeElement;
    return (
      <div data-testid="grounds-near" style={{ color: "var(--nw-ink)" }}>
        <span data-testid="grounds-new-screen" className="sr-only" aria-hidden="true" />
        <ScreenBar coach={coach} />
        {/* The screen's name, for a screen reader: the map layout has no room for the visible title. */}
        <h1 className="sr-only">{coach ? COACH_FIND_TITLE : "Where are you playing?"}</h1>
        <div data-nw-grounds-sheet="">
          {/* At the full snap the sheet covers the whole map: its controls are hidden, so they leave the tab order (and only then). */}
          <div data-testid="grounds-near-map" data-nw-sheet-map="" inert={sheetFull}>
            {mapMounted ? mapSurface : null}
            {notice}
          </div>
          <SheetFloat>
            {searchBox}
            {viewChips}
            {searchResultLine}
          </SheetFloat>
          <GroundsSheet onFull={setSheetFull}>
            <div className="flex flex-col" style={{ gap: 14, padding: "0 20px 20px" }}>
              {savedBanner}
              {listBlock}
            </div>
          </GroundsSheet>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="grounds-near" style={{ color: "var(--nw-ink)" }}>
      <span data-testid="grounds-new-screen" className="sr-only" aria-hidden="true" />
      <ScreenBar coach={coach} />

      <div className="flex flex-col nw-split" style={{ gap: 14, padding: 20 }}>
        <div className="flex flex-col" style={{ gap: 4 }}>
          <div className="nw-eyebrow">Grounds</div>
          <div className="nw-title-lg">{coach ? COACH_FIND_TITLE : "Where are you playing?"}</div>
        </div>

        {savedBanner}

        {searchBox}

        {viewChips}

        {/* Issue #765 / PR #858 F5 — the shipped #617 line: "N matches" on
            a positive search, "No matches" (with worldwide/clear-filter)
            on a no-match one, above the map exactly as the shipped page
            places it. */}
        {searchResultLine}

        {/* #1193 (D236): clamp(220px, 40vh, 360px) tall with a full-screen
            button; the same map and notice open as the overlay. */}
        {/* #1430: from 1024 this is the detail column, the map holding still while the list scrolls, the opened
            ground's card under it; below that these two wrappers are plain blocks round the same band. */}
        <div data-nw-detail="stick">
          <div data-nw-stick={detailCard ? "card" : ""}>
            <MapFullScreen testId="grounds-near-map" controlInPill>
              {({ closeThen }) => (
                <>
                  {mapMounted ? mapSurface : null}
                  {/* PR #1273 F1: a function notice gets `closeThen`, so an action
                      that opens a dialog leaves full screen first. */}
                  {typeof mapNoticeElement === "function" ? mapNoticeElement({ closeThen }) : mapNoticeElement}
                </>
              )}
            </MapFullScreen>
            {detailCard}
          </div>
        </div>

        {listBlock}
      </div>
    </div>
  );
}

// PR #858 F1 — the drawn window: 6 rows, matching the artboard's own M/N/O/
// O/P/S example over its 41-ground fixture (41 - 6 = 35, "35 more, S to
// W"). Local to the screen (not `lib/groundsNearYou.js`'s own
// PLAYED_LIST_VISIBLE_CAP, still 6 too) because the reveal-on-tap/reveal-
// on-"more" state that consumes it is UI state, not shared arithmetic.
const PLAYED_LIST_VISIBLE_CAP_LOCAL = 6;

function DirectoryFailedCard({ onRetry }) {
  return (
    <div
      data-testid="grounds-near-directory-failed"
      className="flex flex-col items-center text-center"
      style={{
        gap: 6,
        padding: "20px 16px",
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card)",
      }}
    >
      <div
        aria-hidden="true"
        className="flex items-center justify-center"
        style={{ width: 44, height: 44, borderRadius: 14, background: "var(--nw-line-faint)" }}
      >
        <TriangleAlert size={20} style={{ color: "var(--nw-alert)" }} />
      </div>
      <div className="nw-body-strong">{DIRECTORY_FAILED_TITLE}</div>
      <p className="nw-help">{DIRECTORY_FAILED_BODY}</p>
      {/* Small fix, PR #858 review round 1 — this card used to accept
          `onRetry` and never call it. */}
      <button
        type="button"
        onClick={onRetry}
        data-testid="grounds-near-directory-failed-retry"
        style={{ marginTop: 4, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline" }}
      >
        Try again
      </button>
    </div>
  );
}

function PlayedSection({
  playedTotal,
  groups,
  unlettered,
  rail,
  loaded,
  showRail,
  selectedLetter,
  footer,
  onRevealAll,
  onJumpToLetter,
  searching,
  term,
  playedNarrowedCount,
  onClearTerm,
  onOpenGround,
}) {
  const noMatch = Boolean(term) && !searching && playedNarrowedCount === 0;

  // #1232 (D247): a player who has played nowhere gets one muted line, not
  // a header over a count of 0, the explainer, the A-Z rail and an empty
  // list. Nearby and Add a ground sit directly under it.
  // `loaded` first: before `/venues/played` answers, `playedTotal` is 0 for
  // everyone, so a player WITH played grounds would be told they have none.
  if (!loaded) return null;
  if (playedTotal === 0) {
    return (
      <p className="nw-help" data-testid="grounds-near-played-empty">
        {GROUNDS_PLAYED_EMPTY}
      </p>
    );
  }

  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <div className="flex items-baseline justify-between">
        <div
          data-testid="grounds-near-played"
          style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}
        >
          Grounds you&apos;ve played
        </div>
        <div data-testid="grounds-near-played-count" style={{ fontSize: 12, fontWeight: 700, color: "var(--nw-navy)" }}>
          {playedTotal}
        </div>
      </div>
      <p className="nw-help">{GROUNDS_PLAYED_HELPER}</p>

      {searching ? (
        <SearchingSkeleton />
      ) : noMatch ? (
        // D138 (overlay decisions) — the drawn played list's own empty
        // answer to a search that narrowed it to nothing: this screen's
        // one filter is the search box itself.
        <div
          data-testid="grounds-near-played-no-match"
          className="flex flex-col"
          style={{ gap: 8, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
        >
          <p className="nw-help">
            Nothing matches this filter.{" "}
            <button
              type="button"
              onClick={onClearTerm}
              style={CLEAR_IT_STYLE}
            >
              Clear it
            </button>{" "}
            to see your whole record.
          </p>
        </div>
      ) : (
        <div className="flex" style={{ gap: 8 }}>
          <div className="flex flex-col" style={{ gap: 6, flexGrow: 1, minWidth: 0 }}>
            {/* D165 — `rail` (from `railLetterIndex`) is always the full
                A-Z, `has` marking whether played OR nearby has anything;
                `groups.has(r.letter)` narrows that down to the letters
                THIS window has actually revealed (`visibleRows`/
                `visibleGroups`, capped by `PLAYED_LIST_VISIBLE_CAP_LOCAL`,
                a narrower set than the full `playedNarrowed` list `rail`
                itself is built from). */}
            {rail
              .filter((r) => groups.has(r.letter))
              .map((r) => (
                <div key={r.letter} className="flex flex-col" style={{ gap: 6 }}>
                  <div className="flex items-center" style={{ gap: 8 }}>
                    <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 12, color: "var(--nw-navy)" }}>
                      {r.letter}
                    </div>
                    <div style={{ flexGrow: 1, height: 1, background: "var(--nw-line)" }} />
                  </div>
                  {(groups.get(r.letter) || []).map((g) => (
                    <PlayedRow key={g.ground_id || `freetext-${g.name}`} ground={g} onOpenGround={onOpenGround} />
                  ))}
                </div>
              ))}
            {/* D165 — a name starting with a digit or symbol sorts at the
                end of the list, with no letter header at all (never a
                "#"). */}
            {unlettered.map((g) => (
              <PlayedRow key={g.ground_id || `freetext-${g.name}`} ground={g} onOpenGround={onOpenGround} />
            ))}
          </div>
          {showRail && <LetterRail rail={rail} selectedLetter={selectedLetter} onJump={onJumpToLetter} />}
        </div>
      )}

      {footer && (
        <button
          type="button"
          data-testid="grounds-near-more"
          onClick={onRevealAll}
          className="flex items-center justify-between"
          style={{
            minHeight: 44,
            padding: "0 14px",
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-control)",
            cursor: "pointer",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--nw-navy)" }}>{footer}</span>
          <span style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>keep scrolling</span>
        </button>
      )}
    </div>
  );
}

/**
 * D165 — the signed-off drawing's own compact A-Z scrubber: all 26
 * letters, ~9px type, ~300px tall, the current letter in navy and every
 * other one muted (an empty letter is dimmed the SAME as a present one —
 * the drawing draws no third, "disabled" shade at all; "muted but still
 * active" is a behavioural claim, not a visual one). Review round 3 (F3):
 * replaces the previous per-letter WCAG-tap-target design (26 real
 * `<button>`s, ~700px) — the ruling's own words: "the tap area is the
 * full strip rather than 26 separate 24px buttons, and the column does
 * not grow to about 700px". ONE element with pointer handlers computing
 * which of the 26 letters a touch/drag position maps to; the 26 `<span>`s
 * inside are pure layout, `pointerEvents: "none"`, never their own
 * targets.
 *
 * Review round 3, final narrow round (A3) — two more findings:
 *
 * (c) The 16px visual strip (D165's own drawn width) is narrower than
 * WCAG 2.5.8's 24px floor. D165 is an ORCHESTRATOR RULING on the #903/#908
 * review (decisions-2026-09-24.md), not an Andreas sign-off — its own
 * header says outright that Andreas may reverse it — so this is a
 * provisional trade-off, not a settled design decision. The HIT AREA does
 * not have to share the visual width regardless: the outer
 * element (`containerRef`, the actual pointer/keyboard target, `role=
 * "slider"`) is sized to at least 24px wide and invisible; the drawn 16px
 * pill sits centred inside it, `aria-hidden` and `pointerEvents: "none"`,
 * decorative only. `letterAtClientY` measures the OUTER box, so a touch
 * landing in the invisible margin still resolves to a real letter.
 *
 * F11 (keyboard): the slider tracks its OWN `selectedLetter` index — see
 * `GroundsNearYouScreen`'s own header comment on `selectedLetter` /
 * `suppressScrollTrackingRef` for why that value is no longer purely
 * scroll-derived. Arrow keys move exactly one letter (A through Z all
 * reachable); Home/End jump to the ends; PageUp/PageDown move five.
 * Landing on an empty letter still calls `onJump` with the CHOSEN letter
 * (not a resolved one) — `onJump` (`jumpToLetter`) itself is what applies
 * "jumps the list to the next letter that exists" while leaving
 * `selectedLetter` on the choice. `aria-valuetext` says so explicitly
 * ("no grounds, showing M") rather than silently reporting the empty
 * letter with no explanation, or the resolved one with no memory of what
 * was actually asked for.
 */
function LetterRail({ rail, selectedLetter, onJump }) {
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const letterAtClientY = (clientY) => {
    const el = containerRef.current;
    if (!el || rail.length === 0) return null;
    const rect = el.getBoundingClientRect();
    if (rect.height === 0) return null;
    const ratio = Math.min(Math.max((clientY - rect.top) / rect.height, 0), 0.999999);
    return rail[Math.floor(ratio * rail.length)]?.letter ?? null;
  };

  const handleAt = (clientY) => {
    const letter = letterAtClientY(clientY);
    if (letter) onJump(letter);
  };

  const selectedIndex = Math.max(0, rail.findIndex((r) => r.letter === selectedLetter));
  const step = (delta) => {
    const next = rail[Math.min(rail.length - 1, Math.max(0, selectedIndex + delta))];
    if (next) onJump(next.letter);
  };

  const selectedHasGrounds = rail[selectedIndex]?.has ?? false;
  const resolvedLetter = selectedHasGrounds ? selectedLetter : nextPresentLetter(selectedLetter, rail);
  // F11 — never "top of the list", never a raw non-letter value: always
  // either the chosen letter, or an explicit "no grounds" note naming
  // what the list actually jumped to (or that nothing after it has
  // anything, if `resolvedLetter` comes back null).
  const valueText = selectedHasGrounds
    ? selectedLetter
    : resolvedLetter
      ? `No grounds, showing ${resolvedLetter}`
      : `No grounds from ${selectedLetter} onward`;

  return (
    // Review round 2 (A3), F14 — the LAYOUT box drawn beside the played
    // rows must stay exactly 16px (the drawing's own 350 - 8 - 16 = 326px
    // row width, flush with the right edge). Widening this box to fit the
    // hit area (the round-3 (c) fix, since reverted) pushed every played
    // row 12px narrower and left the 16px pill sitting 6px in from the
    // edge — a real visual regression this box must never reintroduce.
    // The >=24px hit area is instead an ABSOLUTELY POSITIONED layer
    // beneath, 4px wider each side (16 + 4 + 4 = 24) — it extends outward
    // from this box without ever being part of its layout width.
    <div
      className="flex items-center justify-center"
      style={{
        width: 16,
        height: 300,
        flexShrink: 0,
        position: "sticky",
        top: 96,
        alignSelf: "flex-start",
      }}
    >
      <div
        ref={containerRef}
        data-testid="grounds-near-rail"
        role="slider"
        aria-label="Jump to a letter"
        aria-valuemin={0}
        aria-valuemax={rail.length - 1}
        aria-valuenow={selectedIndex}
        aria-valuetext={valueText}
        tabIndex={0}
        className="flex items-center justify-center"
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: -4,
          right: -4,
          touchAction: "none",
          cursor: "pointer",
          background: "none",
          border: "none",
        }}
        onPointerDown={(e) => {
          draggingRef.current = true;
          e.currentTarget.setPointerCapture?.(e.pointerId);
          handleAt(e.clientY);
        }}
        onPointerMove={(e) => {
          if (draggingRef.current) handleAt(e.clientY);
        }}
        onPointerUp={() => {
          draggingRef.current = false;
        }}
        onPointerCancel={() => {
          draggingRef.current = false;
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            step(1);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            step(-1);
          } else if (e.key === "Home") {
            e.preventDefault();
            onJump(rail[0].letter);
          } else if (e.key === "End") {
            e.preventDefault();
            onJump(rail[rail.length - 1].letter);
          } else if (e.key === "PageDown") {
            e.preventDefault();
            step(5);
          } else if (e.key === "PageUp") {
            e.preventDefault();
            step(-5);
          }
        }}
      >
        {/* (c) — the drawn 16px pill: purely visual, never its own pointer
            target (the hit layer above already captures every
            touch/click across its full >=24px width, including the
            invisible margin around this). */}
        <div
          aria-hidden="true"
          className="flex flex-col items-center"
          style={{
            width: 16,
            height: "100%",
            padding: "4px 0",
            justifyContent: "space-between",
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-pill)",
            pointerEvents: "none",
          }}
        >
          {rail.map((r) => (
            <span
              key={r.letter}
              data-testid={`grounds-near-rail-${r.letter}`}
              style={{
                pointerEvents: "none",
                fontSize: 9,
                fontWeight: r.letter === selectedLetter ? 800 : 600,
                lineHeight: 1.25,
                color: r.letter === selectedLetter ? "var(--nw-navy)" : "var(--nw-ink-faint)",
              }}
            >
              {r.letter}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function PlayedRow({ ground, onOpenGround }) {
  const facts = groundFactsLine({ placeType: ground.placeType, facilities: ground.facilities });
  const rowId = ground.ground_id || `freetext-${ground.name}`;
  // D167 G3 — only a row with a real `ground_id` opens the card; a
  // free-text played row (legacy history with no directory match) stays
  // inert, exactly as #765 left it.
  const openable = Boolean(ground.ground_id && onOpenGround);
  return (
    <div
      data-grounds-letter={(ground.name || "").trim().charAt(0).toUpperCase()}
      data-testid={`grounds-near-played-row-${rowId}`}
      role={openable ? "button" : undefined}
      tabIndex={openable ? 0 : undefined}
      onClick={openable ? () => onOpenGround(ground) : undefined}
      onKeyDown={
        openable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenGround(ground);
              }
            }
          : undefined
      }
      className="flex flex-col"
      style={{
        gap: 3,
        padding: "10px 14px",
        minHeight: "var(--nw-size-tap)", // #1210: 43px drawn, 44px tap box (D237.1)
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card)",
        cursor: openable ? "pointer" : undefined,
      }}
    >
      <div className="flex items-baseline justify-between" style={{ gap: 10 }}>
        <div className="flex items-center" style={{ gap: 6, minWidth: 0 }}>
          <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 14, color: "var(--nw-ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {ground.name}
          </div>
          {isArchived({ archived_at: ground.archivedAt }) && (
            <span
              data-testid={`archived-badge-${rowId}`}
              style={{
                flexShrink: 0,
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                padding: "2px 6px",
                borderRadius: "var(--nw-radius-pill)",
                background: "var(--nw-line-faint)",
                color: "var(--nw-ink-muted)",
              }}
            >
              {ARCHIVED_LABEL}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: "var(--nw-ink-muted)", whiteSpace: "nowrap" }}>
          {playedCountLabel(ground.matches)}
        </div>
      </div>
      {facts && (
        <div style={{ lineHeight: 1.45 }}>
          <span style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>{facts.line}</span>
          {facts.notes.map((n) => (
            <span key={n} style={{ fontSize: 12, color: "var(--nw-ink-faint)" }}>
              {" "}
              {n}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SearchingSkeleton() {
  return (
    <div data-testid="grounds-directory-searching" className="flex flex-col" style={{ gap: 10 }}>
      <SkeletonBar width="45%" />
      <div
        className="flex flex-col"
        style={{ gap: 12, padding: 16, background: "var(--nw-page)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
      >
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex justify-between" style={{ gap: 12 }}>
            <SkeletonBar width="55%" />
            <SkeletonBar width="22%" />
          </div>
        ))}
      </div>
    </div>
  );
}

function SkeletonBar({ width }) {
  return <div aria-hidden="true" style={{ height: 14, width, borderRadius: 6, background: "var(--nw-line)" }} />;
}

function NearbySection({ nearMe, nearbyList, newGroundIds, onAddHere, onOpenGround }) {
  // "locating" has no card of its own here — R-003 (one gold element): the
  // REAL gold button already reads "Finding you…" while `nearMe.status ===
  // "locating"` (see the `ActionButton` above), which is this screen's one
  // built representation of that state. `GroundsNearYou.dc.html`'s own
  // "While the phone finds where it is" block is commentary — a second,
  // gold-styled button drawn for documentation, never meant to exist twice
  // on the real page.
  if (nearMe.status !== "ready") return null;

  if (nearbyList.length === 0) {
    return (
      <div
        data-testid="grounds-near-nothing"
        className="flex flex-col"
        style={{ gap: 10, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
      >
        <p className="nw-help">{NOTHING_NEAR_TITLE}</p>
        <button
          type="button"
          data-testid="grounds-near-nothing-add"
          onClick={onAddHere}
          style={{ alignSelf: "flex-start", background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline" }}
        >
          {NOTHING_NEAR_ACTION}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <div
        data-testid="grounds-near-me-banner"
        style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)", padding: "10px 12px", background: "var(--nw-line-faint)", borderRadius: "var(--nw-radius-control)" }}
      >
        Nearest first — {nearbyList.length} ground{nearbyList.length === 1 ? "" : "s"} within {NEAR_ME_RADIUS_KM} km of you.
      </div>
      <div
        data-testid="grounds-near-nearby"
        style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}
      >
        Nearby
      </div>
      <div className="flex flex-col" style={{ gap: 8 }}>
        {nearbyList.map((g) => (
          <NearbyRow key={g.ground_id || g.name} ground={g} isNew={Boolean(g.ground_id && newGroundIds?.has(g.ground_id))} onOpenGround={onOpenGround} />
        ))}
      </div>
    </div>
  );
}

function NearbyRow({ ground, isNew = false, onOpenGround }) {
  const chips = nearbyFactChips({ placeType: ground.place_type, facilities: ground.facilities });
  // D167 G3 — a nearby row always carries a directory `ground_id`
  // (`useGroundsNearYou.js`'s near-me directory read), unlike a legacy
  // free-text played row, so this one is unconditionally openable once a
  // handler is supplied.
  const openable = Boolean(ground.ground_id && onOpenGround);
  return (
    <div
      // Review round 3 (#908, F4) — the same attribute `PlayedRow` carries,
      // so the rail's jump-to-letter and current-letter tracking both see
      // Nearby rows too: "the rail indexes every row the list shows,
      // played and nearby" (D165's own words).
      data-grounds-letter={(ground.name || "").trim().charAt(0).toUpperCase()}
      data-testid={`grounds-near-nearby-row-${ground.ground_id || ground.name}`}
      role={openable ? "button" : undefined}
      tabIndex={openable ? 0 : undefined}
      onClick={openable ? () => onOpenGround(ground) : undefined}
      onKeyDown={
        openable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenGround(ground);
              }
            }
          : undefined
      }
      className="flex flex-col"
      style={{ gap: 6, padding: "12px 14px", background: "var(--nw-surface)", border: `1px solid ${isNew ? "var(--nw-navy)" : "var(--nw-line)"}`, borderRadius: "var(--nw-radius-card)", cursor: openable ? "pointer" : undefined }}
    >
      <div className="flex items-baseline justify-between" style={{ gap: 10 }}>
        <div className="flex items-baseline" style={{ minWidth: 0, gap: 8, fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 15, color: "var(--nw-ink)" }}>
          <span style={{ minWidth: 0 }}>{ground.name}</span>
          {isNew && (
            <span
              data-testid={`grounds-near-new-${ground.ground_id}`}
              style={{ flexShrink: 0, whiteSpace: "nowrap", padding: "2px 8px", background: "var(--nw-navy)", color: "#fff", fontFamily: "'DM Sans', 'Segoe UI', sans-serif", fontSize: 11, fontWeight: 600, borderRadius: "var(--nw-radius-pill)" }}
            >
              New
            </span>
          )}
        </div>
        <NearbyTownAndDistance ground={ground} />
      </div>
      <div className="flex flex-wrap" style={{ gap: 6 }}>
        {chips.map((c) => (
          <span
            key={c.label}
            style={{
              display: "inline-flex",
              padding: "4px 10px",
              background: c.muted ? "var(--nw-surface)" : "var(--nw-line-faint)",
              border: c.muted ? "1px dashed var(--nw-line-strong)" : "none",
              color: c.muted ? "var(--nw-ink-faint)" : "var(--nw-ink)",
              fontSize: 11,
              fontWeight: 600,
              borderRadius: "var(--nw-radius-pill)",
            }}
          >
            {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * The bar and the ribbon under it. The player's is the navy-on-white bar with
 * the player ribbon, as it always was; the coach's (#1288) goes home to the
 * portal and wears the coach ribbon with Grounds current, the pattern
 * `LearnChrome` uses.
 */
function ScreenBar({ coach }) {
  if (coach) return <CoachBar />;
  return (
    <>
      <TopBar
        variant="white"
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        endControls={BAR_END_CONTROLS}
        data-testid="grounds-near-topbar"
      />
      <DestinationRow current="grounds" />
    </>
  );
}

function CoachBar() {
  const { user } = useAuth();
  return (
    <>
      <TopBar
        variant="white"
        homeHref={homePathForRole(user?.role)}
        homeLabel={`CricketCompass — go to ${homeLabelForRole(user?.role)}`}
        endControls={BAR_END_CONTROLS}
        data-testid="grounds-near-topbar"
      />
      <CoachRibbon current="grounds" />
    </>
  );
}

/** The town on the right of a Nearby row with its distance under it (round-12 GroundsNearDistance). */
function NearbyTownAndDistance({ ground }) {
  const label = nearbyDistanceLabel(ground);
  return (
    <div className="flex flex-col items-end" style={{ gap: 1, minWidth: 0, maxWidth: "45%" }}>
      {/* G5: a free-text town gives way (ellipsis); the distance below stays whole. */}
      <div style={{ maxWidth: "100%", fontSize: 12, color: "var(--nw-ink-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ground.city || ""}</div>
      {label && (
        <div
          data-testid={`grounds-near-nearby-distance-${ground.ground_id || ground.name}`}
          style={{ flexShrink: 0, fontSize: 14, fontWeight: 700, color: "var(--nw-navy)", whiteSpace: "nowrap" }}
        >
          {label}
        </div>
      )}
    </div>
  );
}

const SECTION_HEADER_STYLE = { fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)" };

/**
 * #1288 — the coach's "Grounds you added" (round-12 CoachGrounds). A coach logs
 * no matches, so the list they keep is the grounds they added. No rail, no
 * window, no count anywhere but the header; an empty list is one muted line
 * and nothing else (D247).
 */
function AddedSection({ loaded, added, narrowed, truncated, term, searching, onOpenGround }) {
  if (!loaded || searching) {
    return (
      <div data-testid="grounds-coach-added-loading" className="flex flex-col" style={{ gap: 8 }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="flex flex-col"
            style={{ gap: 8, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
          >
            <SkeletonBar width="50%" />
            <SkeletonBar width="35%" />
          </div>
        ))}
      </div>
    );
  }
  // D367.9b: a search with a term gets the board's no-match card even when
  // the coach has added nothing; only an empty box shows the muted line.
  if (added.length === 0 && !term) {
    return (
      <p className="nw-help" data-testid="grounds-coach-added-empty">
        {COACH_ADDED_EMPTY}
      </p>
    );
  }
  return (
    <div data-testid="grounds-coach-added" className="flex flex-col" style={{ gap: 8 }}>
      {/* The board's no-match state draws the card alone, with no header over it (D259). */}
      {!(term && narrowed.length === 0) && (
        <div data-testid="grounds-coach-added-header" style={SECTION_HEADER_STYLE}>
          {coachAddedHeader(added.length, truncated)}
        </div>
      )}
      {term && narrowed.length === 0 ? (
        <div
          data-testid="grounds-coach-added-no-match"
          className="flex flex-col"
          style={{ gap: 4, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
        >
          <p className="nw-body-strong">{coachNoMatchLine(term)}</p>
          <p className="nw-help">{COACH_NO_MATCH_HINT}</p>
        </div>
      ) : (
        narrowed.map((g) => <AddedRow key={g.ground_id} ground={g} onOpenGround={onOpenGround} />)
      )}
    </div>
  );
}

function AddedRow({ ground, onOpenGround }) {
  const openable = Boolean(ground.ground_id && onOpenGround);
  return (
    <div
      data-testid={`grounds-coach-added-row-${ground.ground_id}`}
      role={openable ? "button" : undefined}
      tabIndex={openable ? 0 : undefined}
      onClick={openable ? () => onOpenGround(ground) : undefined}
      onKeyDown={
        openable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenGround(ground);
              }
            }
          : undefined
      }
      className="flex items-center justify-between"
      style={{ gap: 10, minHeight: 56, padding: "8px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)", cursor: openable ? "pointer" : undefined }}
    >
      <div className="min-w-0 flex flex-col" style={{ gap: 3 }}>
        <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 15, color: "var(--nw-ink)" }}>{ground.name}</div>
        <div data-testid={`grounds-coach-added-facts-${ground.ground_id}`} style={{ fontSize: 12, lineHeight: 1.45, color: "var(--nw-ink-muted)" }}>
          {coachFactsLine({ placeType: ground.placeType, facilities: ground.facilities })}
        </div>
      </div>
      <ChevronRight size={18} strokeWidth={2} style={{ color: "var(--nw-navy)", flexShrink: 0 }} aria-hidden="true" />
    </div>
  );
}
